import React, { useState } from 'react';
import { Backdrop, Snackbar, CircularProgress, Typography, Modal, Box, TextField, Stack, Autocomplete, List, ListItem, ListItemText, IconButton, Paper, Table, TableContainer, TableBody, TableRow, TableCell, Tooltip, TableSortLabel, TableHead, Divider, DialogContent, DialogTitle, Dialog, DialogActions, DialogContentText, Button, Checkbox } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Alert from 'src/components/Alert';
import Page1 from 'src/components/Page';
import axios from 'src/utils/axios';
import SearchIcon from '@mui/icons-material/Search';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CableIcon from '@mui/icons-material/Cable';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SaveIcon from '@mui/icons-material/Save';
import DejaVuSans from 'src/assets/font/DejaVuSans.ttf';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import EmailIcon from '@mui/icons-material/Email';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import Chip from '@mui/material/Chip';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import trLocale from 'date-fns/locale/tr';
import * as pdfjsLib from 'pdfjs-dist';
import html2pdf from 'html2pdf.js';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import TechGptChatbox from 'src/components/TechGpt/TechGptChatbox'
import AddIcon from '@mui/icons-material/Add';
import { Viewer } from '@react-pdf-viewer/core';
import { toolbarPlugin } from '@react-pdf-viewer/toolbar';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DownloadIcon from '@mui/icons-material/Download';
import { dosyaTuruBul, base64Bytes, dosyaIndir } from 'src/utils/dokumanTuru';
// Stil dosyaları
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/toolbar/lib/styles/index.css';

const localeMap = {
  tr: trLocale
};

/* ---------------------------------------------------------------------------
   DÖKÜMAN YÖNETİMİ RENK PALETİ

   Uygulamanın primary rengi Arçelik turuncusu (#FF9800) ve light/main/dark
   üçü de aynı değer. Tek başına kullanıldığında sayfa gri kalıyor, durum
   bilgisi de renkten okunamıyor. Bu yüzden marka turuncusunu birincil
   eylemlere ayırıp, durumlar için ayrı bir anlam paleti tanımlıyoruz.

   Kural: turuncu = "bunu yapabilirsin", diğer renkler = "durum bu".
   --------------------------------------------------------------------------- */
const renk = {
  marka: '#FF9800',
  markaKoyu: '#E07C00',
  markaZemin: '#FFF4E5',

  // Anlam renkleri — birbirinden ayırt edilebilir tonlar
  basari: '#2E7D32', basariZemin: '#E8F5E9',   // Synced / aktif
  bekleme: '#B26A00', beklemeZemin: '#FFF8E1', // InReview — onay bekliyor
  islem: '#0277BD', islemZemin: '#E1F5FE',     // Processing / Indexing
  hata: '#C62828', hataZemin: '#FDECEA',       // Failed
  notr: '#546E7A', notrZemin: '#ECEFF1',       // bilinmiyor / pasif

  // Yüzeyler — nötr griler hafif mavi eğimli, turuncuyla çakışmıyor
  zemin: '#F4F6F8',
  yuzey: '#FFFFFF',
  yuzey2: '#FAFBFC',
  cizgi: '#E1E6EB',
  cizgiKoyu: '#CBD3DA',
  yazi: '#1B2733',
  yazi2: '#4A5A69',
  yazi3: '#7A8A99'
};

// Servis durumunu renklendirir. Etiket BİLEREK İngilizce kalıyor: kullanıcılar
// servisin terimlerini (Synced, InReview, Failed...) zaten biliyor ve
// Türkçeleştirmek ekranı servis cevabıyla eşleştirmeyi zorlaştırır.
const durumBilgisi = (s) => {
  switch (s) {
    case 'Synced': return { etiket: 'Synced', renk: renk.basari, zemin: renk.basariZemin };
    case 'InReview': return { etiket: 'InReview', renk: renk.bekleme, zemin: renk.beklemeZemin };
    case 'Processing': return { etiket: 'Processing', renk: renk.islem, zemin: renk.islemZemin };
    case 'Indexing': return { etiket: 'Indexing', renk: renk.islem, zemin: renk.islemZemin };
    case 'Failed': return { etiket: 'Failed', renk: renk.hata, zemin: renk.hataZemin };
    default: return { etiket: s || 'Bilinmiyor', renk: renk.notr, zemin: renk.notrZemin };
  }
};

// Takvimde seçili gün: tema contrastText'i de turuncu olduğu için turuncu
// dairenin içindeki rakam kayboluyordu. Seçili/bugünkü günü açıkça beyaz yazıyoruz.
const takvimSlotProps = {
  textField: { fullWidth: true, size: 'small', color: 'primary', variant: 'outlined' },
  day: {
    sx: {
      '&.Mui-selected': {
        bgcolor: renk.marka, color: '#fff !important', fontWeight: 700,
        '&:hover': { bgcolor: renk.markaKoyu },
        '&:focus': { bgcolor: renk.marka, color: '#fff !important' }
      },
      '&.MuiPickersDay-today': { borderColor: renk.marka }
    }
  }
};

// Sayfa genelinde tekrar eden rozet biçimi.
const rozet = (c, z, dolu) => ({
  height: 24, fontWeight: 700, fontSize: '0.72rem', borderRadius: '6px',
  bgcolor: dolu ? c : z, color: dolu ? '#fff' : c,
  border: '1px solid', borderColor: dolu ? c : c,
  '& .MuiChip-icon': { color: dolu ? '#fff' : c, marginLeft: '6px' }
});

const styles = {
  // Sayfa kendi içinde kaydırılıyor (sol liste, PDF, sağ panel ayrı ayrı).
  // Dışarıda ikinci bir kaydırma çubuğu çıkmasın diye sayfa gövdesi
  // ekrana sabitlenip taşma kapatılıyor; dikey boşluk da kaldırıldı.
  root: () => ({
    paddingTop: 0,
    paddingBottom: 0,
    height: 'calc(100vh - 88px)',   // üstteki uygulama çubuğu kadar pay
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  }),
  backdrop: (theme) => ({
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  }),
};

// Add this modal style after useStyles
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxHeight: '80vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  overflowY: 'auto',
  borderRadius: '8px'
};

const FileManagement = () => {
  const theme = useTheme();

  const toolbarPluginInstance = toolbarPlugin();
  const { Toolbar } = toolbarPluginInstance;

  const toolbarPluginInstance2 = toolbarPlugin(); // 2. PDF viewer için
  const { Toolbar: Toolbar2 } = toolbarPluginInstance2;

  const [loading, setLoading] = useState(false);
  const [uyari, setUyari] = useState(false);
  const [uyariTip, setUyariTip] = useState('default');
  const [file, setFile] = useState(null);
  // Açık dokümanın içerikten tespit edilen türü (null = PDF, ör. yeni yüklenen dosya).
  const [orijinalTur, setOrijinalTur] = useState(null);
  const [fileName, setFileName] = useState("");
  const [documentStatusData, setDocumentStatusData] = useState(null); // object
  const [documentStatus, setDocumentStatus] = useState(''); // fallback hata mesajı
  const [documentSummaryPopUp, setDocumentSummaryPopUp] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [accumulatedUrunListesi, setAccumulatedUrunListesi] = useState([]);
  const [text, setText] = useState("");
  const [llmSummary, setllmSummary] = useState("");
  const [llmDetail, setllmDetail] = useState("");
  const [responseMessage, setResponseMessage] = useState({
    ErrorCode: '0',
    ErrorDescription: 'success_message'
  });
  const [chunks, setChunks] = useState([]);
  const [base64PDF, setBase64PDF] = useState(''); // Base64 PDF çıktısı için yeni state
  const [openModal, setOpenModal] = useState(false);
  const [techGptPopUp, setTechGptPopUp] = useState(false);
  const [bankNoInput, setBankNoInput] = useState("");
  const [bankaAciklamaInput, setBankaAciklamaInput] = useState("");
  const [dosyaLinkAciklamaInput, setDosyaLinkAciklamaInput] = useState("");
  const [bankaKodu, setBankaKodu] = useState("");
  const [documentList, setDocumentList] = useState([]);
  const [bankTypes, setBankTypes] = useState([]);
  const [selectedBankType, setSelectedBankType] = useState(null);
  const [selectedBankFilterType, setSelectedBankFilterType] = useState(null);
  const [mainGroups, setMainGroups] = useState([]);
  const [selectedMainGroupFilter, setSelectedMainGroupFilter] = useState(null);
  const [selectedMainGroups, setSelectedMainGroups] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [models, setModels] = useState([]);
  const [selectedModels, setSelectedModels] = useState([]);

  const [productsData, setProductsData] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productInputValue, setProductInputValue] = useState("");
  const [documentBankNoList, setDocumentBankNoList] = useState([]);

  const [bulkModelUploadPopUp, setBulkModelUploadPopUp] = useState(false);
  const [bulkUserModelCodes, setBulkUserModelCodes] = useState('');

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogAction, setConfirmDialogAction] = useState(null);

  const openConfirmDialog = (action) => {
    setConfirmDialogAction(() => action);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
    setConfirmDialogAction(null);
  };

  const handleConfirmDialogConfirm = () => {
    if (confirmDialogAction) confirmDialogAction();
    setConfirmDialogOpen(false);
    setConfirmDialogAction(null);
  };

  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('BANKAKODU');
  const [modelOrder, setModelOrder] = React.useState('asc');
  const [modelOrderBy, setModelOrderBy] = React.useState('KUL_MODEL_KODU');

  const [bulkUserModelCodeLoading, setBulkUserModelCodeLoading] = useState(false);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [productLoading, setProductLoading] = useState(false);
  const [banktypeLoading, setBanktypeLoading] = useState(false);
  const [mainGroupLoading, setMainGroupLoading] = useState(false);
  const [modelLoading, setModelLoading] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileOutputLoading, setFileOutputLoading] = useState(false);

  // Her işlemin kendi bekleme durumu var; tüm ekranı kilitleyen Backdrop yerine
  // ilgili alan kendi içinde "çalışıyor" gösteriyor. Böylece durum sorgulanırken
  // kullanıcı formu doldurmaya ya da başka doküman açmaya devam edebiliyor.
  const [durumSorgulaniyor, setDurumSorgulaniyor] = useState(false);
  const [indeksleniyor, setIndeksleniyor] = useState(false);
  const [ragYukleniyor, setRagYukleniyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [siliniyor, setSiliniyor] = useState(false);
  const [aktiflikDegisiyor, setAktiflikDegisiyor] = useState(false);
  const [dosyaYukleniyor, setDosyaYukleniyor] = useState(false);
  const [mailGonderiliyor, setMailGonderiliyor] = useState(false);
  const [loadingBankNo, setLoadingBankNo] = useState(null);
  const [teknisyenKocuAktiflik, setTeknisyenKocuAktiflik] = useState(null);
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" style={{ color: '#FF9800' }} />;

  // Orta kolon görünümü: dokümanın orijinali, RAG çıktısı ya da ikisi yan yana.
  const [gorunum, setGorunum] = useState('orijinal'); // 'orijinal' | 'rag' | 'yanyana'
  // Sağ denetçi paneli sekmesi.
  const [sagSekme, setSagSekme] = useState('bilgi');  // 'bilgi' | 'model'
  // Servis durumunun en son ne zaman sorgulandığı (kullanıcı tazeliğini görsün diye).
  const [sonDurumSorgusu, setSonDurumSorgusu] = useState(null);

  // O an AÇIK olan dokümanın banka numarası — dosya adından türetiliyor.
  //
  // bankNoInput'u doğrudan KULLANMIYORUZ: "Ürün Ara" ile ürün seçildiğinde bankNoInput
  // o ürünün tüm dokümanlarının virgüllü listesi oluyor ("65919,75070,...") ve soldaki
  // listeden başka bir doküman açılınca da güncellenmiyor. parseInt virgülde durduğu için
  // güncelleme/aktiflik/mail işlemleri açık doküman yerine listenin İLK dokümanına
  // uygulanıyordu. Doküman bazlı işlemler ve test sohbeti bunu kullanıyor.
  const seciliBankaNo = () => (fileName ? String(fileName).replace(/\.pdf$/i, '').trim() : '');


  const [talepEdilmeBaslangicTarihi, setTalepEdilmeBaslangicTarihi] = useState(
    new Date(new Date().getFullYear(), 0, 1) // Bulunduğumuz yılın ilk ayının ilk günü gelmesi istenmiştir.
  );

  const [talepEdilmeBitisTarihi, setTalepEdilmeBitisTarihi] = useState(
    new Date()
  );

  React.useEffect(() => {
    getUploadedDocumentList();
  }, [talepEdilmeBaslangicTarihi, talepEdilmeBitisTarihi]);


  React.useEffect(() => {
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
  }, []);

  React.useEffect(() => {
    getBankTypes();
    getMainGroups();
    getModels('');
  }, []);

  // Bir doküman açıldığında servis durumunu kendiliğinden sorgula.
  // Kullanıcının ayrıca "Tekrar sorgula" demesi gerekmesin; düğme yalnızca
  // tazeleme için kalıyor. Durum bilinmeden "Onayla ve indeksle" açılmıyor.
  React.useEffect(() => {
    if (fileName && fileName.length > 0) {
      handleGetStatusOfDocument();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileName]);

  const uyariKapat = (event, reason) => {
    if (reason === 'clickaway') return;
    setUyari(false);
  };

  const getUploadedDocumentList = () => {
    if (talepEdilmeBaslangicTarihi && talepEdilmeBitisTarihi) {
      const formattedBaslangicDate = moment(talepEdilmeBaslangicTarihi).format("MM-DD-YYYY");
      const formattedBitisDate = moment(talepEdilmeBitisTarihi).format("MM-DD-YYYY");
      setDocumentLoading(true);
      axios.get(`/GetTeknisyenKocuUploadedFiles/${formattedBaslangicDate}/${formattedBitisDate}`)
        .then((response) => {
          const data = response.data;
          setDocumentList(Array.isArray(data) ? data : []);
          setDocumentLoading(false);
        }).catch((err) => {
          setDocumentLoading(false);
        });
    }
  }

  const getBankTypes = () => {
    setBanktypeLoading(true);
    axios.get('/GetBankTypes')
      .then((response) => {
        const data = response.data;
        setBankTypes(data);
        setBanktypeLoading(false);
      }).catch((err) => {
        setBanktypeLoading(false);
      });
  }

  const getMainGroups = () => {
    setMainGroupLoading(true);
    axios.get('/GetMainProductGroup')
      .then((response) => {
        const data = response.data;
        setMainGroups(data);
        setMainGroupLoading(false);
      }).catch((err) => {
        setMainGroupLoading(false);
      });
  }

  const getModels = (search) => {
    setModelLoading(true);
    axios.get(`GetModelsForDocument?Search=${search}`)
      .then((response) => {
        const data = response.data;
        if (data && data !== '')
          setModels(data);
        setModelLoading(false);
      }).catch((err) => {
        setModelLoading(false);
      });
  }

  const handleUploadFile = (event) => {
    const selectedFile = event.target.files[0];
    // setFileName(selectedFile.name); // dosya adı olarak artık bankaNo dosya adı kullanılacak. Örn "91528.pdf"
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setDosyaYukleniyor(true);
      const fileURL = URL.createObjectURL(selectedFile);
      setOrijinalTur(null);
      setFile(fileURL);

      const formData = new FormData();
      formData.append("file", selectedFile);

      formData.append("MainProductGroup", selectedMainGroups.ANA_GRUP_KODU);
      formData.append("BankType", selectedBankType.BANKATIPI);
      formData.append("BankDescription", bankaAciklamaInput);
      formData.append("BankCode", bankaKodu);
      selectedModels.forEach(m => {
        formData.append("Models", m.MODEL_KODU);
      });

      // const bankaNoArray = bankNoInput
      //   .split(",")
      //   .map(num => num.trim())
      //   .filter(num => num.length > 0)
      //   .map(num => Number(num));

      // formData.append("metadata", JSON.stringify({
      //   BankaNo: bankaNoArray
      // }));

      axios.post("/TeknisyenGptUploadFile", formData
      ).then(response => {
        const data = response.data;
        if (data.ErrorCode === "0") {
          const dosyaAdi = String(data.BankNo) + '.pdf';
          setFileName(dosyaAdi);
          setBankNoInput(String(data.BankNo))
          setResponseMessage({
            ErrorCode: data.ErrorCode,
            ErrorDescription: data.ErrorDescription
          });
          setUyariTip('success');
          setUyari(true);



          setSelectedBankType(null);
          setSelectedMainGroups(null);
          setBankaAciklamaInput("");
          setSelectedModels([]);
          setBankaKodu("");

          getUploadedDocumentList();
        } else {
          setResponseMessage({
            ErrorCode: data.ErrorCode,
            ErrorDescription: data.ErrorDescription
          });
          setUyariTip('error');
          setUyari(true);
        }
        setDosyaYukleniyor(false);
      }).catch((error) => {
        setDosyaYukleniyor(false);
      });
    }
  }

  const handleGetStatusOfDocument = () => {
    if (fileName.length <= 0) {
      setResponseMessage({
        ErrorCode: '1000',
        ErrorDescription: 'Lütfen önce bir dosya yükleyiniz!...'
      });
      setUyariTip('error');
      setUyari(true);
      return;
    }
    setDurumSorgulaniyor(true);
    axios.get(`/GetDocumentStatus/${fileName}`)
      .then(response => {
        const data = response.data;
        if (data?.message === 'success' && data?.content) {
          setDocumentStatusData(data.content);
          // Durum artık sağ panelde sürekli görünüyor; popup'ı kendiliğinden açmıyoruz.
          // Tüm alanları görmek için "Detay" düğmesi var.
          setSonDurumSorgusu(new Date());
        } else {
          setResponseMessage({
            ErrorCode: '1000',
            ErrorDescription: data?.message ?? 'Dosya Bulunamadı!...'
          });
          setUyariTip('error');
          setUyari(true);
        }
        setDurumSorgulaniyor(false);
      })
      .catch(error => {
        setDurumSorgulaniyor(false);
        setDocumentStatusData(null);
      });
  }

  const handleIndexingDocument = () => {
    if (fileName.length <= 0) {
      setResponseMessage({
        ErrorCode: '1000',
        ErrorDescription: 'Lütfen önce bir dosya yükleyiniz!...'
      });
      setUyariTip('error');
      setUyari(true);
      return;
    }
    setIndeksleniyor(true);

    axios.get(`/GetDocumentStatus/${fileName}`)
      .then(statusResponse => {
        const statusData = statusResponse.data;
        if (!(statusData?.message === 'success' && statusData?.content)) {
          setResponseMessage({
            ErrorCode: '1000',
            ErrorDescription: 'Dosya Bulunamadı!...'
          });
          setUyariTip('error');
          setUyari(true);
          setIndeksleniyor(false);
          return;
        }

        // Index öncesi durum kontrolü
        const documentStatus = statusData.content.status;
        if (documentStatus !== "InReview") {
          setResponseMessage({
            ErrorCode: '1001',
            ErrorDescription: `Belge "${documentStatus}" durumunda, kaydetme yapılamaz!`
          });
          setUyariTip('warning');
          setUyari(true);
          setIndeksleniyor(false);
          return;
        }

        const data = {
          fileName: fileName
        };
        axios.post("/TeknisyenGptIndexingDocument", data
        ).then(indexResponse => {
          const result = indexResponse.data;
          if (result.ErrorCode === "0") {
            setResponseMessage({
              ErrorCode: result.ErrorCode,
              ErrorDescription: result.ErrorDescription
            });
            setUyariTip('success');
            setUyari(true);
          } else {
            setResponseMessage({
              ErrorCode: result.ErrorCode,
              ErrorDescription: result.ErrorDescription
            });
            setUyariTip('error');
            setUyari(true);
          }
          setIndeksleniyor(false);
        }).catch((error) => {
          setIndeksleniyor(false);
        });

      })
      .catch(error => {
        setIndeksleniyor(false);
        setDocumentStatusData(null);
      });
  }

  const handleGetRagReadyDocument = () => {
    if (fileName.length <= 0) {
      setResponseMessage({
        ErrorCode: '1000',
        ErrorDescription: 'Lütfen önce bir dosya yükleyiniz!...'
      });
      setUyariTip('error');
      setUyari(true);
      return;
    }

    setFileOutputLoading(true);
    axios.get(`/GetDocumentStatus/${fileName}`)
      .then(statusResponse => {
        const statusData = statusResponse.data;
        if (!(statusData?.message === 'success' && statusData?.content)) {
          setResponseMessage({
            ErrorCode: '1000',
            ErrorDescription: 'Dosya Bulunamadı!...'
          });
          setUyariTip('error');
          setUyari(true);
          setFileOutputLoading(false);
          return;
        }

        // Sonuç Gösterme öncesi durum kontrolü
        const documentStatus = statusData.content.status;
        if (documentStatus == "Pending" || documentStatus == "Failed") {
          setResponseMessage({
            ErrorCode: '1001',
            ErrorDescription: `Belge "${documentStatus}" durumunda, sonuç gösterilemez.`
          });
          setUyariTip('warning');
          setUyari(true);
          setFileOutputLoading(false);
          return;
        }

        axios.get(`/GetRagReadyDocument/${fileName}`, {
          responseType: 'blob'
        })
          .then(response => {
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            setPdfUrl(url);
            setFileOutputLoading(false);
          })
          .catch(error => {
            setFileOutputLoading(false);
            setResponseMessage({
              ErrorCode: '1000',
              ErrorDescription: 'PDF alınırken hata oluştu.'
            });
            setUyariTip('error');
            setUyari(true);
          });

      })
      .catch(error => {
        setFileOutputLoading(false);
        setDocumentStatusData(null);
      });
  };


  const handleDeleteDocument = () => {
    if (fileName.length <= 0) {
      setResponseMessage({
        ErrorCode: '1000',
        ErrorDescription: 'Lütfen önce bir dosya yükleyiniz!...'
      });
      setUyariTip('error');
      setUyari(true);
      return;
    }

    const data = {
      fileName: fileName,
      bankNo: seciliBankaNo()
    };
    setSiliniyor(true);
    axios.post("/SetDeleteDocument", data
    ).then(response => {
      const result = response.data;

      if (result.ErrorCode === "0") {
        setResponseMessage({
          ErrorCode: result.ErrorCode,
          ErrorDescription: result.ErrorDescription
        });
        setUyariTip('success');
        setUyari(true);

        // Temizlik İşlemleri
        setPdfUrl(null);
        setDocumentStatusData(null);
        setFile(null);
        setFileName("");
        setBankNoInput("");
        setSelectedBankType(null);
        setSelectedMainGroups(null);
        setBankaAciklamaInput("");

        getUploadedDocumentList();
      } else {
        setResponseMessage({
          ErrorCode: result.ErrorCode,
          ErrorDescription: result.ErrorDescription || "Silme işlemi başarısız!"
        });
        setUyariTip('error');
        setUyari(true);

      }
      setSiliniyor(false);
    }).catch((error) => {
      setSiliniyor(false);
    });
  }

  // Eski Stepper ("Sonuç Göster / Kaydet / Sil") kaldırıldı. Aynı işlemler artık
  // orta kolonun araç çubuğunda duruyor:
  //   Sonuç Göster -> "RAG Çıktısı" görünümü      (handleGetRagReadyDocument)
  //   Kaydet       -> "Onayla ve indeksle"        (handleIndexingDocument)
  //   Sil          -> "Sil"                       (openConfirmDialog + handleDeleteDocument)

  // Bu ekrandaki sohbet yalnızca açık dokümanla yapılır ("Bu dokümanla test sohbeti başlat").
  // Ürünün tüm dokümanlarıyla sohbet Teknisyen Koçu ekranında (ürün arama) yapılıyor.
  const handleChatBox = () => {
    if (!seciliBankaNo()) {
      setResponseMessage({
        ErrorCode: '1000',
        ErrorDescription: 'Lütfen önce listeden bir doküman seçiniz!...'
      });
      setUyariTip('error');
      setUyari(true);
      return;
    }
    setTechGptPopUp(true);
  }

  const handleChangeSelectedDocument = (event, newValue) => {
    setSelectedProduct(null);
    setFile(null);
    setPdfUrl(null);
    setDocumentStatusData(null);
    setDocumentBankNoList([]);

    if (newValue) {
      const dosyaAdi = String(newValue.BANKA_NO) + '.pdf';
      setFileName(dosyaAdi);
      setBankNoInput(String(newValue.BANKA_NO));

      setPdfUrl(null);
      setDocumentStatusData(null);
      setFile(null);

      openDocumentInline(newValue.BANKA_NO)
    } else {
      setFileName("");
      setBankNoInput("");
      setPdfUrl(null);
      setDocumentStatusData(null);
      setFile(null);
      setSelectedBankType(null);
      setSelectedMainGroups(null);
      setBankaKodu("");
      setBankaAciklamaInput("");
      setDosyaLinkAciklamaInput("");
      setSelectedModels([]);
      setTeknisyenKocuAktiflik(null);
    }
  }

  const handleChangeBankType = (newValue) => {
    if (newValue) {
      setSelectedBankType(newValue);
    }
  }

  const handleClearEverything = () => {
    setFile(null);
    setPdfUrl(null);
    setDocumentStatusData(null);
    setSelectedBankType(null)
    setSelectedDocument(null)
    setSelectedMainGroups(null)
    setSelectedProduct(null)
    setTeknisyenKocuAktiflik(null);

    setFileName("");
    setBankNoInput("");
    setBankaAciklamaInput("");
    setDosyaLinkAciklamaInput("");
    setSelectedModels([]);
    setBankaKodu("");
    setProductInputValue("");
    setDocumentBankNoList([]);
  }

  const handleToggleAktiflik = (targetValue) => {
    if (!seciliBankaNo()) return;
    const newAktiflik = targetValue;
    const data = {
      BankaNo: parseInt(seciliBankaNo()),
      isActive: newAktiflik == true ? 1 : 0
    };
    setAktiflikDegisiyor(true);
    axios.post('/SetTeknisyenKocuDokumanAktiflik', data)
      .then(response => {
        const result = response.data;
        if (result.ErrorCode === '0') {
          setTeknisyenKocuAktiflik(newAktiflik);

          setResponseMessage({
            ErrorCode: result.ErrorCode,
            ErrorDescription: result.ErrorDescription
          });
          setUyariTip('success');
          setUyari(true);
        } else {
          setResponseMessage({
            ErrorCode: result.ErrorCode,
            ErrorDescription: result.ErrorDescription || 'İşlem başarısız!'
          });
          setUyariTip('error');
          setUyari(true);
        }
        setAktiflikDegisiyor(false);
      })
      .catch(() => {
        setAktiflikDegisiyor(false);
      });
  };

  const handleUpdateDocument = () => {
    if (!seciliBankaNo()) {
      setResponseMessage({
        ErrorCode: '1000',
        ErrorDescription: 'Lütfen önce listeden bir doküman seçiniz!'
      });
      setUyariTip('error');
      setUyari(true);
      return;
    }

    const data = {
      bankaNo: parseInt(seciliBankaNo()),
      bankaTipi: selectedBankType.BANKATIPI,
      anaGrupKodu: selectedMainGroups.ANA_GRUP_KODU,
      dosyaAciklamasi: bankaAciklamaInput,
      bankaKodu: bankaKodu,
      selectedModels: selectedModels.map(m => m.MODEL_KODU),
      dosyaLinkAciklama: dosyaLinkAciklamaInput ? dosyaLinkAciklamaInput : ''
    };
    setKaydediliyor(true);
    axios.post("/TeknisyenKocuDocumentUpdateArekInfo", data
    ).then(response => {
      const result = response.data;

      if (result.ErrorCode === "0") {
        setResponseMessage({
          ErrorCode: result.ErrorCode,
          ErrorDescription: result.ErrorDescription
        });
        setUyariTip('success');
        setUyari(true);
      } else {
        setResponseMessage({
          ErrorCode: result.ErrorCode,
          ErrorDescription: result.ErrorDescription || "Kaydedilemedi!"
        });
        setUyariTip('error');
        setUyari(true);
      }
      setKaydediliyor(false);
    }).catch((error) => {
      setKaydediliyor(false);
    });
  }

  const openDocumentInline = (bankNo) => {
    setFileLoading(true);
    setLoadingBankNo(bankNo);
    axios.get(`/GetTeknisyenKocuRelatedDocument/${bankNo}`)
      .then((response) => {

        const base64Data = response.data[0].DocumentData;

        setSelectedBankType(bankTypes.find(bank => bank.BANKATIPI === response.data[0].BANKATIPI));
        setSelectedMainGroups(mainGroups.find(group => group.ANA_GRUP_KODU === response.data[0].ANA_GRUP_KODU));
        setBankaAciklamaInput(response.data[0].Description);
        setBankaKodu(response.data[0].BankaKodu);
        setDosyaLinkAciklamaInput(response.data[0].MOBIL_TEXT);
        setTeknisyenKocuAktiflik(response.data[0].TeknisyenKocuAktiflik);
        const rawModels = response.data[0].ModelKodlari;
        let parsedModels = [];
        try {
          parsedModels = rawModels ? JSON.parse(rawModels) : [];
        } catch (e) {
          parsedModels = [];
        }
        setSelectedModels(parsedModels);


        // Base64 -> Blob. Tabloda PDF dışı dosyalar da var; tür içerikten bulunur ve
        // görüntüleme ona göre yapılır (bkz. orijinalPaneli).
        const byteArray = base64Bytes(base64Data || '');
        const tur = dosyaTuruBul(byteArray);
        const url = URL.createObjectURL(new Blob([byteArray], { type: tur.mime }));

        setOrijinalTur(tur);
        setFile(url);
        setFileLoading(false);
        setLoadingBankNo(null);
      })
      .catch((error) => {
        setFileLoading(false);
        setLoadingBankNo(null);
        console.error("Döküman yüklenirken hata oluştu:", error);
      });
  };

  const handleGetProductsBySearchTerm = (searchTerm) => {
    setProductLoading(true);
    axios.get(`/GetProductsBySearchTerm/${searchTerm}`)
      .then((response) => {
        const data = response.data;
        setProductsData(data);
        setProductLoading(false);
      }).catch((err) => {
        setProductLoading(false);
      });
  }

  const handleChangeSelectedProduct = (event, newValue) => {
    setFile(null);
    setPdfUrl(null);
    setDocumentStatusData(null);

    setSelectedProduct(newValue);

    if (newValue) {
      const userModelCode = newValue.KUL_MODEL_KODU;
      setDocumentLoading(true);
      axios.get(`/GetBankNoForTeknisyenKocu/${userModelCode}`)
        .then((response) => {
          const data = response.data;
          setDocumentBankNoList(data ? data : []);

          let bankNoList = [];
          data.map((x) => {
            bankNoList.push(x.BANKANO);
          })
          setBankNoInput(bankNoList.toString());

          setDocumentLoading(false);
        }).catch((err) => {
          setDocumentLoading(false);
        });
    } else {
      setDocumentBankNoList([]);
    }

  }

  const handleSaveBulkUserModelCodes = () => {
    const userModelCodeList = bulkUserModelCodes.split("\n")
      .map(l => l.trim())
      .filter(l => l !== "");

    let fetchedModels = [];
    let completed = 0;

    setBulkUserModelCodeLoading(true);
    userModelCodeList.forEach(modelCode => {
      axios.get(`GetModelsForDocument?Search=${modelCode}`)
        .then((response) => {
          const data = response.data;
          if (data && data.length > 0) {
            fetchedModels.push(data[0]);
          }
          completed++;
          if (completed === userModelCodeList.length) {
            setSelectedModels(prev => {
              const merged = [...prev, ...fetchedModels];
              return merged.filter(
                (item, index, self) =>
                  index === self.findIndex(i => i.MODEL_KODU === item.MODEL_KODU)
              );
            });
            setBulkUserModelCodeLoading(false);
            setBulkModelUploadPopUp(false);
            setBulkUserModelCodes('');
          }
        })
        .catch((err) => {
          completed++;

          if (completed === userModelCodeList.length) {
            setSelectedModels(prev => {
              const merged = [...prev, ...fetchedModels];
              return merged.filter(
                (item, index, self) =>
                  index === self.findIndex(i => i.MODEL_KODU === item.MODEL_KODU)
              );
            });
            setBulkUserModelCodeLoading(false);
            setBulkModelUploadPopUp(false);
            setBulkUserModelCodes('');
          }
        });
    });

  }

  const handleRemoveModel = (modelToRemove) => {
    const newSelection = selectedModels.filter(
      (m) => m.MODEL_KODU !== modelToRemove.MODEL_KODU
    );
    setSelectedModels(newSelection);
  };

  function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  function getComparator(order, orderBy) {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }
  // Sıralama Tetikleyici
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Tıklanınca Sıralama Yönü Iconunu Oluşturan Helper
  const createSortHandler = (property) => (event) => {
    handleRequestSort(property);
  };

  const handleRequestModelSort = (property) => {
    const isAsc = modelOrderBy === property && modelOrder === 'asc';
    setModelOrder(isAsc ? 'desc' : 'asc');
    setModelOrderBy(property);
  };

  const createModelSortHandler = (property) => (event) => {
    handleRequestModelSort(property);
  };

  const handleSendMail = () => {
    // Mail açık dokümana gidiyor (seciliBankaNo her zaman tek numara). Eskiden burada
    // bankNoInput virgüllü liste olduğunda mail engelleniyordu; ürün aramasından sonra
    // tek bir doküman açık olsa bile gönderilemiyordu.
    if (!seciliBankaNo()) {
      setResponseMessage({
        ErrorCode: '1000',
        ErrorDescription: 'Lütfen önce listeden bir doküman seçiniz!'
      });
      setUyariTip('error');
      setUyari(true);
      return;
    }

    if (!file) {
      setResponseMessage({
        ErrorCode: '1000',
        ErrorDescription: 'Lütfen önce dosya seçiniz!'
      });
      setUyariTip('error');
      setUyari(true);
      return;
    }

    const data = {
      BankaNo: parseInt(seciliBankaNo())
    };

    setMailGonderiliyor(true);
    axios.post('/SendDocumentBulletinMail', data)
      .then(response => {
        const result = response.data;
        if (result.ErrorCode === '0') {
          setResponseMessage({
            ErrorCode: result.ErrorCode,
            ErrorDescription: result.ErrorDescription
          });
          setUyariTip('success');
          setUyari(true);
        } else {
          setResponseMessage({
            ErrorCode: result.ErrorCode,
            ErrorDescription: result.ErrorDescription || 'Mail gönderilemedi!'
          });
          setUyariTip('error');
          setUyari(true);
        }
        setMailGonderiliyor(false);
      })
      .catch(() => {
        setMailGonderiliyor(false);
      });
  }

  // ---- Görünüm için türetilen değerler -------------------------------------
  // Servis durumu bilinmeden indeksleme denenmemeli. handleIndexingDocument
  // zaten sunucu tarafında InReview kontrolü yapıyor; burada kullanıcıyı
  // gereksiz bir tur atmaktan kurtarmak için düğmeyi baştan kapatıyoruz.
  const ragDurum = documentStatusData?.status ?? null;
  const indekslenebilir = ragDurum === 'InReview';
  const indekslemeEngeli = !fileName
    ? 'Önce bir doküman seçin'
    : !ragDurum
      ? 'Durum henüz alınamadı — "Tekrar sorgula" ile deneyin'
      : indekslenebilir
        ? ''
        : `Belge "${durumBilgisi(ragDurum).etiket}" durumunda, kaydetme yapılamaz`;

  const durum = durumBilgisi(ragDurum);
  const tarihYaz = (v) => v ? new Date(v).toLocaleString('tr-TR') : '-';

  // Ürün araması bir ürünün TÜM dokümanlarını getirdiği için bankNoInput
  // virgülle ayrılmış uzun bir liste olabiliyor. Ekranda asıl gereken,
  // o an açık olan dokümanın numarası — onu dosya adından türetiyoruz.
  const acikBankaNo = seciliBankaNo();
  const digerDokumanSayisi = Math.max(0, (documentBankNoList?.length || 0) - 1);

  // Solda listelenen dokümanlar (mevcut sıralama mantığı korunuyor)
  const listeKayitlari = stableSort(documentBankNoList, getComparator(order, orderBy));

  // Orta kolonda PDF gösterimi — hem orijinal hem RAG çıktısı aynı bileşenle.
  // PDF olarak açılamayan dosyalarda (tür tanınamadı, PDF bozuk vb.) gösterilen bilgi kutusu.
  const acilamayanDosya = (url, baslik, aciklama, uzanti) => (
    <Box sx={{
      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', gap: 1.25, color: 'text.secondary', p: 3, textAlign: 'center'
    }}>
      <InsertDriveFileIcon sx={{ fontSize: 44, opacity: .35 }} />
      <Typography variant="body2" sx={{ fontWeight: 700, color: renk.yazi }}>{baslik}</Typography>
      <Typography variant="body2" sx={{ maxWidth: 420 }}>{aciklama}</Typography>
      <Button size="small" variant="outlined" startIcon={<DownloadIcon />}
        onClick={() => dosyaIndir(url, `${seciliBankaNo() || 'dokuman'}${uzanti}`)}>
        Dosyayı indir
      </Button>
    </Box>
  );

  const pdfPaneli = (url, plugin, ToolbarBilesen, bos) => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'grey.100' }}>
      {url ? (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
            <ToolbarBilesen />
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
            <Viewer
              fileUrl={url}
              plugins={[plugin]}
              renderError={(hata) => acilamayanDosya(
                url,
                'Dosya PDF olarak görüntülenemedi',
                `İçerik PDF gibi görünüyor ama okunamadı (${hata?.message || 'bilinmeyen hata'}). Dosya bozuk ya da eksik kaydedilmiş olabilir; indirip başka bir uygulamada açmayı deneyebilirsiniz.`,
                '.pdf'
              )}
            />
          </Box>
        </>
      ) : (
        <Box sx={{
          flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', gap: 1, color: 'text.secondary', p: 3, textAlign: 'center'
        }}>
          <InsertDriveFileIcon sx={{ fontSize: 40, opacity: .35 }} />
          <Typography variant="body2">{bos}</Typography>
        </Box>
      )}
    </Box>
  );

  // Orijinal dosya paneli: PDF'ler görüntüleyicide, görsel ve metinler doğrudan, tarayıcının
  // gösteremediği türler (Word, Excel...) tür bilgisi ve indirme düğmesiyle gösterilir.
  const orijinalPaneli = () => {
    const bos = 'Listeden bir doküman seçin.';
    if (!file || !orijinalTur || orijinalTur.tur === 'pdf') {
      return pdfPaneli(file, toolbarPluginInstance, Toolbar, bos);
    }

    // Desteklenmeyen türler (exe, boş, GIF, zip...) listelerde gizleniyor; yine de açılırsa
    // içerik gösterilmez ve indirme sunulmaz.
    if (!orijinalTur.destekleniyor) {
      return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'grey.100' }}>
          <Box sx={{
            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: 1, color: 'text.secondary', p: 3, textAlign: 'center'
          }}>
            <InsertDriveFileIcon sx={{ fontSize: 44, opacity: .35 }} />
            <Typography variant="body2" sx={{ fontWeight: 700, color: renk.yazi }}>Desteklenmeyen dosya türü</Typography>
            <Typography variant="body2">
              {orijinalTur.tur === 'bos' ? 'Bu kayıt için dosya verisi bulunmuyor.' : `Bu kayıt bir ${orijinalTur.etiket}; Teknisyen Koçu'nda kullanılmıyor.`}
            </Typography>
          </Box>
        </Box>
      );
    }

    let icerik;
    if (orijinalTur.tur === 'resim') {
      icerik = (
        <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
          <Box component="img" src={file} alt={`Doküman ${seciliBankaNo()}`}
            sx={{ maxWidth: '100%', bgcolor: '#fff', boxShadow: 1 }} />
        </Box>
      );
    } else {
      icerik = acilamayanDosya(file, `Bu dosya bir ${orijinalTur.etiket}`,
        'Tarayıcı bu türü ekranda gösteremiyor. Dosyayı indirip ilgili uygulamada açabilirsiniz.',
        orijinalTur.uzanti);
    }

    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'grey.100' }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: .75,
          borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper'
        }}>
          <Chip size="small" label={orijinalTur.etiket}
            sx={{ bgcolor: renk.beklemeZemin, color: renk.bekleme, fontWeight: 700 }} />
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            PDF değil
          </Typography>
          <Button size="small" startIcon={<DownloadIcon />}
            onClick={() => dosyaIndir(file, `${seciliBankaNo() || 'dokuman'}${orijinalTur.uzanti}`)}>
            İndir
          </Button>
        </Box>
        {icerik}
      </Box>
    );
  };

  return (
    <Page1 sx={styles.root} title={'Döküman Yönetimi'}>
      <Backdrop sx={styles.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={uyari}
        autoHideDuration={10000}
        onClose={uyariKapat}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={uyariKapat} variant={uyariTip} message={responseMessage.ErrorDescription} />
      </Snackbar>

      {/* ================= ÜST BAR ================= */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
        px: 3, py: 1.75, bgcolor: renk.yuzey, flexShrink: 0,
        borderBottom: `1px solid ${renk.cizgi}`
      }}>
        <Box sx={{
          width: 42, height: 42, borderRadius: '10px', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: renk.markaZemin, color: renk.markaKoyu
        }}>
          <FolderOpenIcon />
        </Box>
        <Box sx={{ mr: 'auto', minWidth: 0 }}>
          <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.15, color: renk.yazi }}>
            Döküman Yönetimi
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: renk.yazi3, letterSpacing: .2 }}>
            Teknisyen Koçu bilgi bankası
          </Typography>
        </Box>
        <Tooltip title="Sıfırdan yeni bir doküman yükle">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { handleClearEverything(); }}
            sx={{
              bgcolor: renk.marka, color: '#fff', fontWeight: 700, px: 2.5,
              boxShadow: 'none', '&:hover': { bgcolor: renk.markaKoyu, boxShadow: 'none' }
            }}
          >
            Yeni Doküman
          </Button>
        </Tooltip>
      </Box>

      {/* ================= 3 KOLON ================= */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '360px minmax(0,1fr)', lg: '400px minmax(0,1fr) 400px' },
        gap: '1px', bgcolor: renk.cizgi,
        // Kalan yüksekliği tamamen doldur; kaydırma yalnızca kolonların içinde
        // olsun diye minHeight:0 şart (grid çocukları taşmasın).
        flex: 1, minHeight: 0, overflow: 'hidden'
      }}>

        {/* ---------- SOL: filtreler + liste ---------- */}
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden', bgcolor: 'background.paper' }}>
          <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap['tr']}>
                <DatePicker
                  label={'Başlangıç tarihi'}
                  format={'dd.MM.yyyy'}
                  value={talepEdilmeBaslangicTarihi}
                  onChange={date => { setTalepEdilmeBaslangicTarihi(date); }}
                  slotProps={takvimSlotProps}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap['tr']}>
                <DatePicker
                  label={'Bitiş tarihi'}
                  format={'dd.MM.yyyy'}
                  value={talepEdilmeBitisTarihi}
                  onChange={date => { setTalepEdilmeBitisTarihi(date); }}
                  slotProps={takvimSlotProps}
                />
              </LocalizationProvider>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Autocomplete
                sx={{ flex: 1, minWidth: 0 }}
                size="small"
                options={mainGroups}
                getOptionLabel={(option) => option?.ANA_GRUP || ""}
                value={selectedMainGroupFilter}
                onChange={(event, newValue) => { setSelectedMainGroupFilter(newValue); }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ürün Grubu"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {mainGroupLoading ? <CircularProgress color="primary" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Autocomplete
                sx={{ flex: 1, minWidth: 0 }}
                size="small"
                options={bankTypes}
                getOptionLabel={(option) => option.ACIKLAMA || ""}
                value={selectedBankFilterType}
                onChange={(event, newValue) => { setSelectedBankFilterType(newValue); }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Banka Tipi"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {banktypeLoading ? <CircularProgress color="primary" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            {/* Ürün Ara — kendi satırında, model kodu/açıklama uzun olabiliyor */}
            <Autocomplete
              size="small"
              options={productsData || []}
              getOptionLabel={(option) => option.TANIM || ''}
              value={selectedProduct || null}
              inputValue={productInputValue}
              onInputChange={(e, newValue, reason) => {
                setProductInputValue(newValue ?? "");
                if (reason === "input") {
                  if ((newValue?.length ?? 0) > 2) { handleGetProductsBySearchTerm(newValue); }
                }
                if (reason === "clear") {
                  handleChangeSelectedProduct(e, null);
                  setProductInputValue("");
                  setBankNoInput("");
                }
              }}
              isOptionEqualToValue={(option, value) => option.MODEL_KODU === value.MODEL_KODU}
              onChange={(event, newValue) => {
                handleChangeSelectedProduct(event, newValue);
                setProductInputValue(newValue ? (newValue["ACIKLAMA"] ?? "") : "");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Ürün Ara"
                  placeholder="Model kodu ya da açıklama"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {productLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* Dosya Seç — "BANKANO - BANKAKODU - AÇIKLAMA" biçiminde geldiği için
                tek satıra sığmıyor; seçenekler iki satır halinde gösteriliyor. */}
            <Autocomplete
              size="small"
              options={
                documentList.filter(doc => {
                  const bankMatch = selectedBankFilterType?.BANKATIPI ? doc.BANKATIPI === selectedBankFilterType.BANKATIPI : true;
                  const groupMatch = selectedMainGroupFilter?.ANA_GRUP_KODU ? doc.URUNGRUBU === selectedMainGroupFilter.ANA_GRUP_KODU : true;
                  return bankMatch && groupMatch;
                })
              }
              value={documentList.find(doc => String(doc.BANKA_NO) === bankNoInput) || null}
              getOptionLabel={(option) => option.DOSYA_ADI || ''}
              onChange={(event, newValue) => { handleChangeSelectedDocument(event, newValue); }}
              renderOption={(props, option) => {
                const { key, ...rest } = props;
                const parcalar = String(option.DOSYA_ADI || '').split(' - ');
                const bas = parcalar.length > 2 ? parcalar.slice(0, 2).join(' — ') : (parcalar[0] || '');
                const ad = parcalar.length > 2 ? parcalar.slice(2).join(' - ') : (parcalar[1] || '');
                return (
                  <li key={key} {...rest} style={{ display: 'block', paddingTop: 6, paddingBottom: 6 }}>
                    <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', color: 'text.secondary' }}>
                      {bas}
                    </Typography>
                    <Typography variant="body2" sx={{ display: 'block' }}>{ad}</Typography>
                  </li>
                );
              }}
              slotProps={{ paper: { sx: { minWidth: 420 } } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Dosya Seç"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {documentLoading ? <CircularProgress color="primary" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          {/* Liste başlığı: adet, yenile, sıralama */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
            bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider'
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
              <b>{listeKayitlari.length}</b> doküman
            </Typography>
            <Tooltip title="Listeyi yenile">
              <IconButton size="small" onClick={() => getUploadedDocumentList()}>
                <CableIcon fontSize="small" sx={{ transform: 'rotate(90deg)' }} />
              </IconButton>
            </Tooltip>
            <TableSortLabel
              active={orderBy === 'BANKAKODU'}
              direction={orderBy === 'BANKAKODU' ? order : 'asc'}
              onClick={createSortHandler('BANKAKODU')}
              sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
            >
              Dosya Adı
            </TableSortLabel>
            <TableSortLabel
              active={orderBy === 'BANKANO'}
              direction={orderBy === 'BANKANO' ? order : 'asc'}
              onClick={createSortHandler('BANKANO')}
              sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
            >
              Banka No
            </TableSortLabel>
          </Box>

          {/* Doküman listesi */}
          <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 240 }}>
            {documentLoading && listeKayitlari.length === 0 && (
              <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>
            )}
            {!documentLoading && listeKayitlari.length === 0 && (
              <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                <CloudOffIcon sx={{ fontSize: 32, opacity: .4, mb: 1 }} />
                <Typography variant="body2">Seçilen filtrelerde doküman yok.</Typography>
              </Box>
            )}
            <List disablePadding>
              {listeKayitlari.map((bank, index) => {
                const secili = String(bank.BANKANO) === seciliBankaNo();
                const yukleniyor = loadingBankNo === bank.BANKANO;
                return (
                  <ListItem
                    key={bank.BANKANO || index}
                    onClick={() => {
                      if (loadingBankNo) return;
                      const filename = bank.BANKANO + ".pdf";
                      setFileName(filename);
                      setGorunum('orijinal');
                      setDocumentStatusData(null);
                      setSonDurumSorgusu(null);
                      openDocumentInline(bank.BANKANO);
                    }}
                    sx={{
                      alignItems: 'center', gap: 1.25, px: 1.5, py: 1.25,
                      borderBottom: `1px solid ${renk.cizgi}`,
                      // Marka turuncusu dolu zemin olarak fazla sert; seçimi sol şerit
                      // ile turuncu tonlu hafif zemin taşıyor.
                      borderLeft: '3px solid',
                      borderLeftColor: secili ? renk.marka : 'transparent',
                      bgcolor: secili ? renk.markaZemin : 'transparent',
                      cursor: yukleniyor ? 'wait' : 'pointer',
                      opacity: loadingBankNo && !yukleniyor ? 0.5 : 1,
                      '&:hover': { bgcolor: secili ? renk.markaZemin : renk.yuzey2 }
                    }}
                  >
                    {/* PDF ikonu — doküman başlığındaki kırmızıyla aynı renk,
                        seçili satırda zemini koyulaşıyor */}
                    <Box sx={{
                      width: 32, height: 32, borderRadius: '7px', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: secili ? renk.hata : renk.hataZemin,
                      color: secili ? '#fff' : renk.hata
                    }}>
                      {yukleniyor
                        ? <CircularProgress size={16} sx={{ color: secili ? '#fff' : renk.hata }} />
                        : <InsertDriveFileIcon sx={{ fontSize: 18 }} />}
                    </Box>

                    <ListItemText
                      disableTypography
                      sx={{ my: 0, minWidth: 0 }}
                      primary={
                        <Tooltip title={bank.BANKAKODU || ''} placement="top-start">
                          <Typography
                            sx={{
                              fontSize: '0.8125rem', fontWeight: secili ? 700 : 500,
                              color: secili ? renk.markaKoyu : renk.yazi, lineHeight: 1.35,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                            }}
                          >
                            {bank.BANKAKODU}
                          </Typography>
                        </Tooltip>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: .75, mt: .25 }}>
                          <Typography sx={{
                            fontFamily: 'monospace', fontSize: '0.7rem', fontWeight: 600,
                            color: renk.yazi3, letterSpacing: .3
                          }}>
                            {bank.BANKANO}
                          </Typography>
                          {yukleniyor && (
                            <Typography sx={{ fontSize: '0.68rem', color: renk.islem, fontWeight: 600 }}>
                              açılıyor…
                            </Typography>
                          )}
                        </Box>
                      }
                    />

                    {secili && !yukleniyor && (
                      <CheckCircleIcon sx={{ fontSize: 18, color: renk.marka, flexShrink: 0 }} />
                    )}
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Box>

        {/* ---------- ORTA: doküman ---------- */}
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden', bgcolor: 'background.paper' }}>
          {/* Başlık + durum rozetleri */}
          <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${renk.cizgi}`, bgcolor: renk.yuzey }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <InsertDriveFileIcon sx={{ color: fileName ? renk.hata : renk.yazi3, fontSize: 26 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                  fontSize: '1.05rem', fontWeight: 700, color: renk.yazi, lineHeight: 1.25,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}>
                  {bankaAciklamaInput || fileName || 'Doküman seçilmedi'}
                </Typography>
                {fileName && (
                  <Typography sx={{ fontSize: '0.75rem', color: renk.yazi3, fontFamily: 'monospace' }}>
                    {fileName}{bankaKodu ? ` · ${bankaKodu}` : ''}
                  </Typography>
                )}
              </Box>
              {/* Tema contrastText'i turuncu olduğu için contained butonlarda yazı
                  turuncuya dönüyordu; rengi burada açıkça beyaza sabitliyoruz. */}
              <Tooltip title="Bu dokümanla test sohbeti başlat">
                <span>
                  <Button size="small" variant="contained" disableElevation startIcon={<TextFieldsIcon />}
                    disabled={!fileName}
                    onClick={() => { handleChatBox(); }}
                    sx={{
                      bgcolor: renk.islem, color: '#fff', fontWeight: 700,
                      '& .MuiButton-startIcon': { color: '#fff' },
                      '&:hover': { bgcolor: '#01579B' },
                      '&.Mui-disabled': { bgcolor: renk.notrZemin, color: renk.yazi3 }
                    }}>
                    Sohbet
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Doküman bilgisini ilgili birime mail olarak gönder">
                <span>
                  <Button size="small" variant="outlined"
                    startIcon={mailGonderiliyor ? <CircularProgress size={14} sx={{ color: renk.islem }} /> : <EmailIcon />}
                    disabled={!fileName || mailGonderiliyor}
                    onClick={() => { handleSendMail(); }}
                    sx={{
                      color: renk.islem, borderColor: renk.islem, fontWeight: 700,
                      '&:hover': { bgcolor: renk.islemZemin, borderColor: renk.islem }
                    }}>
                    Mail Gönder
                  </Button>
                </span>
              </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', gap: .75, flexWrap: 'wrap', mt: 1.25, alignItems: 'center' }}>
              {acikBankaNo && (
                <Chip size="small" label={`Banka No ${acikBankaNo}`}
                  sx={{ ...rozet(renk.yazi2, renk.yuzey2, false), fontFamily: 'monospace' }} />
              )}
              {digerDokumanSayisi > 0 && (
                <Tooltip title={`Bu ürüne bağlı ${digerDokumanSayisi} doküman daha var, soldaki listede`}>
                  <Chip size="small" label={`+${digerDokumanSayisi} doküman`}
                    sx={rozet(renk.notr, renk.notrZemin, false)} />
                </Tooltip>
              )}
              {selectedBankType?.ACIKLAMA && (
                <Chip size="small" label={selectedBankType.ACIKLAMA} sx={rozet(renk.yazi2, renk.yuzey2, false)} />
              )}
              {selectedMainGroups?.ANA_GRUP && (
                <Chip size="small" label={selectedMainGroups.ANA_GRUP} sx={rozet(renk.yazi2, renk.yuzey2, false)} />
              )}

              <Box sx={{ width: '1px', height: 18, bgcolor: renk.cizgi, mx: .5 }} />

              {/* Servis durumu — dolu rozet, renkten okunuyor.
                  Teknisyen Koçu aktifliği burada TEKRARLANMIYOR; sağ panelde
                  zaten hem yazıyor hem de oradan değiştiriliyor. */}
              <Tooltip title={ragDurum ? `Servisin bildirdiği durum: ${ragDurum}` : 'Durum henüz alınamadı'}>
                <Chip size="small" label={durum.etiket} sx={rozet(durum.renk, durum.zemin, true)} />
              </Tooltip>
            </Box>
          </Box>

          {/* Araç çubuğu: görünüm seçimi + yayına alma */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 1,
            borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap'
          }}>
            <Box sx={{
              display: 'flex', borderRadius: '8px', overflow: 'hidden',
              border: `1px solid ${renk.cizgiKoyu}`, bgcolor: renk.yuzey
            }}>
              {[
                { k: 'orijinal', t: 'Orijinal', ip: 'Yüklenen PDF dosyası' },
                { k: 'rag', t: 'RAG Çıktısı', ip: 'Yapay zekânın dokümandan çıkardığı metin' },
                { k: 'yanyana', t: 'Yan yana', ip: 'İkisini karşılaştırarak incele' }
              ].map(g => (
                <Tooltip key={g.k} title={g.ip}>
                  <Button
                    size="small"
                    disableElevation
                    onClick={() => {
                      setGorunum(g.k);
                      // RAG çıktısı ilk kez isteniyorsa servisten getir (eski "Sonuç Göster")
                      if ((g.k === 'rag' || g.k === 'yanyana') && !pdfUrl) { handleGetRagReadyDocument(); }
                    }}
                    sx={{
                      borderRadius: 0, minWidth: 104, fontWeight: 700, fontSize: '0.75rem',
                      bgcolor: gorunum === g.k ? renk.marka : 'transparent',
                      color: gorunum === g.k ? '#fff' : renk.yazi2,
                      '&:hover': { bgcolor: gorunum === g.k ? renk.markaKoyu : renk.yuzey2 }
                    }}
                  >
                    {g.t}
                  </Button>
                </Tooltip>
              ))}
            </Box>

            {(fileOutputLoading || ragYukleniyor) && <CircularProgress size={18} sx={{ color: renk.marka }} />}

            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title={indekslemeEngeli || 'RAG çıktısını onayla, indekslemeyi başlat ve dokümanı yayına al'}>
                <span>
                  <Button
                    variant="contained"
                    disableElevation
                    startIcon={indeksleniyor ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <CableIcon />}
                    disabled={!indekslenebilir || indeksleniyor}
                    onClick={() => { handleIndexingDocument(); }}
                    sx={{
                      fontWeight: 700, px: 2.5,
                      bgcolor: renk.basari, color: '#fff',
                      '&:hover': { bgcolor: '#1B5E20' },
                      '&.Mui-disabled': { bgcolor: renk.notrZemin, color: renk.yazi3 }
                    }}
                  >
                    Onayla ve indeksle
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Dokümanı bilgi bankasından ve arşivden kalıcı olarak sil">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={siliniyor ? <CircularProgress size={15} sx={{ color: renk.hata }} /> : <DeleteForeverIcon />}
                    disabled={!fileName || siliniyor}
                    onClick={() => openConfirmDialog(handleDeleteDocument)}
                    sx={{
                      fontWeight: 700, color: renk.hata, borderColor: renk.hata,
                      '&:hover': { bgcolor: renk.hataZemin, borderColor: renk.hata }
                    }}
                  >
                    Sil
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>

          {/* Sahne */}
          <Box sx={{ flex: 1, minHeight: 420, overflow: 'hidden' }}>
            {gorunum === 'yanyana' ? (
              <Box sx={{
                display: 'grid', gap: '1px', bgcolor: 'divider', height: '100%',
                gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }
              }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
                  <Typography variant="caption" sx={{
                    px: 1.5, py: .75, fontWeight: 700, letterSpacing: .5, color: 'text.secondary',
                    bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider'
                  }}>
                    ORİJİNAL DOSYA
                  </Typography>
                  {orijinalPaneli()}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
                  <Typography variant="caption" sx={{
                    px: 1.5, py: .75, fontWeight: 700, letterSpacing: .5, color: 'text.secondary',
                    bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider'
                  }}>
                    RAG ÇIKTISI
                  </Typography>
                  {pdfPaneli(pdfUrl, toolbarPluginInstance2, Toolbar2, 'RAG çıktısı henüz oluşturulmadı.')}
                </Box>
              </Box>
            ) : gorunum === 'rag'
              ? pdfPaneli(pdfUrl, toolbarPluginInstance2, Toolbar2, 'RAG çıktısı henüz oluşturulmadı.')
              : orijinalPaneli()}
          </Box>
        </Box>

        {/* ---------- SAĞ: denetçi ---------- */}
        <Box sx={{
          display: { xs: 'flex', md: 'flex' }, flexDirection: 'column', minWidth: 0,
          overflow: 'hidden', bgcolor: 'background.paper',
          gridColumn: { xs: '1 / -1', lg: 'auto' }
        }}>
          <Box sx={{ display: 'flex', borderBottom: `1px solid ${renk.cizgi}`, bgcolor: renk.yuzey }}>
            {[
              { k: 'bilgi', t: 'BİLGİLER', ip: 'Dosya bilgileri ve servis durumu' },
              { k: 'model', t: `MODELLER (${selectedModels?.length || 0})`, ip: 'Bu dokümanla eşleşen ürün modelleri' }
            ].map(s => (
              <Tooltip key={s.k} title={s.ip}>
                <Button
                  onClick={() => setSagSekme(s.k)}
                  sx={{
                    flex: 1, borderRadius: 0, py: 1.5, fontWeight: 700, fontSize: '0.75rem',
                    letterSpacing: .5,
                    color: sagSekme === s.k ? renk.markaKoyu : renk.yazi3,
                    bgcolor: sagSekme === s.k ? renk.markaZemin : 'transparent',
                    borderBottom: '2px solid',
                    borderColor: sagSekme === s.k ? renk.marka : 'transparent',
                    '&:hover': { bgcolor: sagSekme === s.k ? renk.markaZemin : renk.yuzey2 }
                  }}
                >
                  {s.t}
                </Button>
              </Tooltip>
            ))}
          </Box>

          {/* --- BİLGİLER --- */}
          {sagSekme === 'bilgi' && (
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
              {/* Teknisyen Koçu aktifliği — zemin her durumda aynı (nötr),
                  aktif/pasif bilgisini yalnızca rozetin rengi taşıyor. */}
              {teknisyenKocuAktiflik !== null && (() => {
                const canToggle = [1, 15, 16, 17, 21, 22].includes(Number(selectedBankType?.BANKATIPI));
                return (
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, mb: 2,
                    borderRadius: '8px', border: `1px solid ${renk.cizgi}`, bgcolor: renk.yuzey2
                  }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: renk.yazi }}>
                        Teknisyen Koçu
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: renk.yazi3, mt: .25 }}>
                        {!canToggle ? 'Bu banka tipi bilgi bankasına gönderilmiyor'
                          : teknisyenKocuAktiflik ? 'Teknisyenler bu dokümanla sohbet edebilir'
                            : 'Sohbette kullanılmıyor'}
                      </Typography>
                    </Box>
                    {aktiflikDegisiyor ? (
                      <CircularProgress size={20} sx={{ color: renk.marka }} />
                    ) : (
                      <Tooltip title={!canToggle ? 'Bu banka tipinde değiştirilemez'
                        : teknisyenKocuAktiflik ? 'Pasife al' : 'Aktife al'}>
                        <Chip
                          icon={teknisyenKocuAktiflik ? <CheckCircleIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
                          label={teknisyenKocuAktiflik ? 'AKTİF' : 'PASİF'}
                          size="small"
                          onClick={canToggle ? () => handleToggleAktiflik(!teknisyenKocuAktiflik) : undefined}
                          sx={{
                            ...rozet(teknisyenKocuAktiflik ? renk.basari : renk.hata,
                              teknisyenKocuAktiflik ? renk.basariZemin : renk.hataZemin, true),
                            cursor: canToggle ? 'pointer' : 'default'
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                );
              })()}

              {/* Servis durumu — zemin nötr, rengi rozet taşıyor */}
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1, p: 1.25,
                borderRadius: '8px 8px 0 0', border: `1px solid ${renk.cizgi}`, borderBottom: 0,
                bgcolor: renk.yuzey2
              }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: .6, color: renk.yazi3 }}>
                  SERVİS DURUMU
                </Typography>
                <Chip size="small" label={durum.etiket} sx={rozet(durum.renk, durum.zemin, true)} />
                <Typography sx={{ ml: 'auto', fontSize: '0.68rem', color: renk.yazi3, fontFamily: 'monospace' }}>
                  {durumSorgulaniyor ? 'sorgulanıyor…' : (sonDurumSorgusu
                    ? sonDurumSorgusu.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
                    : '—')}
                </Typography>
                <Tooltip title="Durumu yapay zekâ servisinden yeniden sorgula">
                  <span>
                    <IconButton size="small" disabled={!fileName || durumSorgulaniyor}
                      onClick={() => { handleGetStatusOfDocument(); }}
                      sx={{ color: renk.islem }}>
                      {durumSorgulaniyor
                        ? <CircularProgress size={16} sx={{ color: renk.islem }} />
                        : <SearchIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              {/* Durum detayları — kendi sınırları olan kart */}
              <Box sx={{
                border: `1px solid ${renk.cizgi}`, borderRadius: '0 0 8px 8px',
                bgcolor: renk.yuzey, p: 1.5, mb: 2
              }}>
                {documentStatusData ? (
                  <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '96px 1fr', gap: 1, alignItems: 'start' }}>
                      <Typography sx={{ fontSize: '0.72rem', color: renk.yazi3 }}>Durum</Typography>
                      <Chip size="small" label={durum.etiket}
                        sx={{ ...rozet(durum.renk, durum.zemin, false), justifySelf: 'start' }} />

                      <Typography sx={{ fontSize: '0.72rem', color: renk.yazi3 }}>Özet</Typography>
                      <Typography sx={{ fontSize: '0.78rem', color: renk.yazi2 }} align="justify">
                        {documentStatusData?.summary || 'Özet şu anda gelmiyor AI Ekibi servise ekleyecek...'}
                      </Typography>

                      <Typography sx={{ fontSize: '0.72rem', color: renk.yazi3 }}>Oluşturulma</Typography>
                      <Typography sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: renk.yazi }}>
                        {tarihYaz(documentStatusData?.latestProcessedVersionCreatedAt)}
                      </Typography>

                      <Typography sx={{ fontSize: '0.72rem', color: renk.yazi3 }}>Onaylanma</Typography>
                      <Typography sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: renk.yazi }}>
                        {tarihYaz(documentStatusData?.latestProcessedVersionApprovedAt)}
                      </Typography>

                      <Typography sx={{ fontSize: '0.72rem', color: renk.yazi3 }}>İşlem bitiş</Typography>
                      <Typography sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: renk.yazi }}>
                        {tarihYaz(documentStatusData?.latestProcessedVersionProcessingFinishedAt)}
                      </Typography>

                      <Typography sx={{ fontSize: '0.72rem', color: renk.yazi3 }}>Versiyon</Typography>
                      <Typography sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: renk.yazi }}>
                        {documentStatusData?.latestProcessedVersionNumber ?? '-'}
                      </Typography>
                    </Box>
                    <Button size="small" sx={{ mt: 1, color: renk.islem, fontWeight: 700, px: 0 }}
                      onClick={() => setDocumentSummaryPopUp(true)}>
                      Tüm alanları göster
                    </Button>
                  </>
                ) : (
                  <Typography sx={{ fontSize: '0.75rem', color: renk.yazi3 }}>
                    {durumSorgulaniyor ? 'Servis durumu alınıyor…' : 'Durum bilgisi alınamadı. Yenileme düğmesine basın.'}
                  </Typography>
                )}
              </Box>

              {/* Bölüm başlığı — eski tasarımda da vardı */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25, mt: .5 }}>
                <Typography sx={{
                  fontSize: '0.7rem', fontWeight: 800, letterSpacing: .7, color: renk.yazi3,
                  whiteSpace: 'nowrap'
                }}>
                  DOSYA BİLGİLERİ
                </Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: renk.cizgi }} />
              </Box>

              {/* Dosya — salt okunur, yüklenen PDF değiştirilemiyor */}
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.25, p: 1.25, mb: .75,
                border: `1px solid ${renk.cizgi}`, borderRadius: '8px', bgcolor: renk.yuzey2
              }}>
                <InsertDriveFileIcon sx={{ color: renk.hata }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: '0.8125rem', fontWeight: 600, color: renk.yazi,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                  }}>
                    {fileName || 'Dosya seçilmedi'}
                  </Typography>
                </Box>
                {dosyaYukleniyor && <CircularProgress size={16} sx={{ color: renk.islem }} />}
              </Box>
              <Typography sx={{ fontSize: '0.72rem', color: renk.yazi3, display: 'block', mb: 2 }}>
                Yüklenen PDF değiştirilemez. Düzeltme gerekiyorsa yeni doküman olarak yükleyin.
              </Typography>

              {/* Düzenlenebilir alanlar */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="Banka Numarası" disabled fullWidth size="small" variant="outlined"
                    value={seciliBankaNo()}
                  />
                  <TextField
                    label="Banka Kodu" fullWidth size="small" variant="outlined"
                    value={bankaKodu}
                    onChange={(e) => { setBankaKodu(e.target.value); }}
                  />
                </Box>

                <Autocomplete
                  size="small"
                  options={bankTypes}
                  getOptionLabel={(option) => option.ACIKLAMA || ""}
                  value={selectedBankType}
                  onChange={(event, newValue) => { setSelectedBankType(newValue); }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Banka Tipi"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {banktypeLoading ? <CircularProgress color="primary" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  size="small"
                  options={mainGroups}
                  getOptionLabel={(option) => option?.ANA_GRUP || ""}
                  value={selectedMainGroups}
                  onChange={(event, newValue) => { setSelectedMainGroups(newValue); }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Ürün Grubu"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {mainGroupLoading ? <CircularProgress color="primary" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <TextField
                  label="Dosya Açıklaması" fullWidth multiline rows={3} size="small" variant="outlined"
                  value={bankaAciklamaInput}
                  onChange={(e) => { setBankaAciklamaInput(e.target.value); }}
                />

                <TextField
                  label="Dosya Link Alanı" fullWidth size="small" variant="outlined"
                  value={dosyaLinkAciklamaInput}
                  onChange={(e) => { setDosyaLinkAciklamaInput(e.target.value); }}
                />

                <Box sx={{ display: 'flex', gap: 1, mt: .5 }}>
                  <Tooltip title="Banka kodu, tip, grup, açıklama ve model eşleşmelerini kaydet">
                    <span style={{ flex: 1 }}>
                      <Button
                        fullWidth variant="contained" disableElevation
                        endIcon={kaydediliyor ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <SaveIcon />}
                        disabled={!fileName || kaydediliyor}
                        onClick={() => { handleUpdateDocument() }}
                        sx={{
                          bgcolor: renk.marka, color: '#fff', fontWeight: 700, py: 1,
                          '&:hover': { bgcolor: renk.markaKoyu },
                          '&.Mui-disabled': { bgcolor: renk.notrZemin, color: renk.yazi3 }
                        }}
                      >
                        KAYDET
                      </Button>
                    </span>
                  </Tooltip>
                  <input
                    accept="application/pdf" id="fileUpload" type="file" style={{ display: 'none' }}
                    onChange={handleUploadFile}
                    onClick={(e) => e.target.value = null}
                  />
                  <label htmlFor="fileUpload">
                    <Tooltip title="PDF seç ve yeni doküman olarak yükle">
                      <Button component="span" variant="outlined" endIcon={<UploadFileIcon />}
                        sx={{
                          fontWeight: 700, py: 1, color: renk.islem, borderColor: renk.islem,
                          '&:hover': { bgcolor: renk.islemZemin, borderColor: renk.islem }
                        }}>
                        YÜKLE
                      </Button>
                    </Tooltip>
                  </label>
                  <Tooltip title="Formu temizle, sıfırdan doküman girişine başla">
                    <Button variant="outlined" onClick={() => { handleClearEverything() }}
                      sx={{
                        minWidth: 44, py: 1, color: renk.yazi2, borderColor: renk.cizgiKoyu,
                        '&:hover': { bgcolor: renk.yuzey2, borderColor: renk.yazi3 }
                      }}>
                      <AddIcon />
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          )}

          {/* --- MODELLER --- */}
          {sagSekme === 'model' && (
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
                <Autocomplete
                  sx={{ flex: 1, minWidth: 0 }}
                  size="small"
                  multiple
                  options={models}
                  value={selectedModels || []}
                  renderTags={() => null}
                  isOptionEqualToValue={(option, value) => option.MODEL_KODU === value.MODEL_KODU}
                  getOptionLabel={(option) => option?.ACIKLAMA || ""}
                  onChange={(event, newValue) => { setSelectedModels(newValue); }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input" && (newInputValue.length > 2 || newInputValue.length === 0)) {
                      getModels(newInputValue);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Eşlenecek Grup/Model Ekle"
                      placeholder={selectedModels?.length > 0 ? "Başka model ekle..." : "Model ara..."}
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {modelLoading ? <CircularProgress color="primary" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <Tooltip title="Toplu model yükleme">
                  <IconButton color="primary" onClick={() => setBulkModelUploadPopUp(true)}>
                    <LibraryAddIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: 'grey.50' }}>
                        <TableSortLabel
                          active={modelOrderBy === 'KUL_MODEL_KODU'}
                          direction={modelOrderBy === 'KUL_MODEL_KODU' ? modelOrder : 'asc'}
                          onClick={createModelSortHandler('KUL_MODEL_KODU')}
                        >
                          Model Kodu
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ bgcolor: 'grey.50' }}>
                        <TableSortLabel
                          active={modelOrderBy === 'ACIKLAMA'}
                          direction={modelOrderBy === 'ACIKLAMA' ? modelOrder : 'asc'}
                          onClick={createModelSortHandler('ACIKLAMA')}
                        >
                          Açıklama
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ bgcolor: 'grey.50' }} align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(!selectedModels || selectedModels.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                          Henüz model eşleştirilmemiş.
                        </TableCell>
                      </TableRow>
                    )}
                    {stableSort(selectedModels || [], getComparator(modelOrder, modelOrderBy)).map((model, index) => (
                      <TableRow key={model.KUL_MODEL_KODU || index} hover>
                        <TableCell sx={{ py: .75 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: .75 }}>
                            <CableIcon sx={{ fontSize: 15, color: renk.islem, flexShrink: 0 }} />
                            <Chip size="small" label={model.KUL_MODEL_KODU}
                              sx={{
                                fontFamily: 'monospace', fontWeight: 700, height: 22,
                                fontSize: '0.72rem', borderRadius: '5px',
                                bgcolor: renk.islemZemin, color: renk.islem,
                                border: `1px solid ${renk.islem}33`
                              }} />
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: renk.yazi2, fontSize: '0.78rem', py: .75 }}>
                          {model.ACIKLAMA}
                        </TableCell>
                        <TableCell align="right" sx={{ py: .75 }}>
                          <Tooltip title="Bu modeli dokümandan kaldır">
                            <IconButton size="small" onClick={() => handleRemoveModel(model)}
                              sx={{ color: renk.yazi3, '&:hover': { color: renk.hata, bgcolor: renk.hataZemin } }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                Eşleşen model, teknisyen sahada bir fiş açtığında bu dokümanın önerilmesini sağlar.
                Grup seçilirse gruptaki tüm modeller kapsanır.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* ================= DİYALOGLAR ================= */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}
        aria-labelledby="modal-title" aria-describedby="modal-description">
        <Box sx={modalStyle}>
          <Typography id="modal-title" variant="h6" component="h2" gutterBottom>
            Doküman İçeriği
          </Typography>
          <Box id="modal-description" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{text}</Typography>
          </Box>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={() => setOpenModal(false)}>Kapat</Button>
          </Box>
        </Box>
      </Modal>

      <Dialog open={documentSummaryPopUp} onClose={() => setDocumentSummaryPopUp(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{"Döküman Özeti"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2">{'Durum:'}</Typography>
              <Chip size="small" label={`${durum.etiket} (${documentStatusData?.status ?? '-'})`}
                sx={rozet(durum.renk, durum.zemin, true)} />
            </Box>
            <Box>
              <Typography variant="subtitle2">{'Özet:'}</Typography>
              <Typography variant="body2" color="text.secondary" align='justify'>
                {documentStatusData?.summary ? documentStatusData?.summary : 'Özet şu anda gelmiyor AI Ekibi servise ekleyecek...'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">{'Oluşturulma Tarihi:'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {tarihYaz(documentStatusData?.latestProcessedVersionCreatedAt)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">{'Onaylanma Tarihi:'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {tarihYaz(documentStatusData?.latestProcessedVersionApprovedAt)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">{'İşlem Bitiş Tarihi:'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {tarihYaz(documentStatusData?.latestProcessedVersionProcessingFinishedAt)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">{'Versiyon:'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {documentStatusData?.latestProcessedVersionNumber ?? '-'}
              </Typography>
            </Box>
            {documentStatusData?.latestProcessedVersionFailureReason && (
              <Box>
                <Typography variant="subtitle2">{'Hata Sebebi:'}</Typography>
                <Typography variant="body2" color="error" align='justify'>
                  {documentStatusData.latestProcessedVersionFailureReason}
                </Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocumentSummaryPopUp(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkModelUploadPopUp} onClose={() => setBulkModelUploadPopUp(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{"Toplu Model Yükleme"}</DialogTitle>
        <DialogContent dividers>
          <TextField
            label='Toplu Model Kodları'
            placeholder="Model kodlarını alt alta veya virgülle ayırarak yapıştırın"
            fullWidth multiline rows={8} variant="outlined"
            value={bulkUserModelCodes}
            onChange={(e) => setBulkUserModelCodes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkModelUploadPopUp(false)}>Vazgeç</Button>
          <Button variant="contained" onClick={() => handleSaveBulkUserModelCodes()}
            startIcon={bulkUserModelCodeLoading ? <CircularProgress size={16} color="inherit" /> : <PlaylistAddCheckIcon />}>
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDialogOpen} onClose={handleConfirmDialogClose}>
        <DialogTitle>Emin misiniz?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bu doküman bilgi bankasından ve arşivden kalıcı olarak silinecek. Bu işlem geri alınamaz.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDialogClose} color="primary">Hayır</Button>
          <Button onClick={handleConfirmDialogConfirm} color="secondary" variant="contained">Evet</Button>
        </DialogActions>
      </Dialog>

      {techGptPopUp && (
        <TechGptChatbox
          open={techGptPopUp}
          onClose={() => setTechGptPopUp(false)}
          bankaNo={seciliBankaNo()}
        />
      )}
    </Page1>
  );
};

export default FileManagement;
